import type { QueryClient } from '@tanstack/react-query';

/**
 * Cache identity for the library's server state.
 *
 * One key per workspace: the catalog already comes back with this workspace's
 * states, so there is nothing else to key on (spec 104 research R1).
 */
export const libraryKeys = {
  all: (workspaceId: string) => ['library', workspaceId] as const,
  items: (workspaceId: string) => ['library', workspaceId, 'items'] as const,
  /**
   * One file of one item, keyed by the item's digest (spec 105 contract).
   *
   * The digest changes when any of the item's files change, so a library that
   * has moved on re-reads and one that has not does not. Without it a reader
   * who kept a drawer open across an upload would be shown yesterday's text.
   */
  file: (
    workspaceId: string,
    kind: string,
    key: string,
    path: string,
    digest: string,
  ) => ['library', workspaceId, 'file', kind, key, path, digest] as const,
  /** What taking a shared item down would take with it (spec 213). */
  takeDownPreview: (workspaceId: string, kind: string, key: string) =>
    ['library', workspaceId, 'take-down', kind, key] as const,
};

/** Re-read the catalog after something outside this feature changed it (spec 206). */
export function invalidateLibrary(
  queryClient: QueryClient,
  workspaceId: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: libraryKeys.all(workspaceId),
  });
}
